import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { apiClient } from '@/lib/api/client'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { ArtworkPreview } from '@/features/films/StudioComponents'
import { WorldStudioPage } from './WorldStudioPage'

type Progress = {
  steps: { id: string; label: string; complete: boolean }[]
  completed: number
  total: number
  percent: number
  label: string
  generationStatus?: string
  previewReady: boolean
  complete: boolean
  error?: string
  filmRequested: boolean
  repairAvailable?: boolean
  corrections?: string[]
}
type World = {
  id: string
  title: string
  artist: string
  source_image_url: string
  stage: string
  updated_at: string
  preview_ready: boolean
  complete: boolean
  film_job_id?: string
  progress: Progress
}
type Page = { items: World[]; total: number; offset: number; limit: number; coverage: string }
type Preview = { previewUrl: string; qrDataUrl: string }
export function WorldBuildProgress({ progress }: { progress: Progress }) {
  return (
    <div className="space-y-3" aria-live="polite">
      <p className="text-sm font-medium">{progress.label}</p>
      <div
        role="progressbar"
        aria-label="World build checkpoints"
        aria-valuenow={progress.completed}
        aria-valuemin={0}
        aria-valuemax={progress.total}
        className="h-2 overflow-hidden rounded-full bg-muted"
      >
        <div
          className="h-full bg-emerald-600 transition-all"
          style={{ width: `${progress.percent}%` }}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        {progress.completed} of {progress.total} checkpoints complete · not a time estimate
      </p>
      <ol className="grid gap-2 text-sm sm:grid-cols-2">
        {progress.steps.map((step) => (
          <li
            key={step.id}
            className={step.complete ? 'text-emerald-800' : 'text-muted-foreground'}
          >
            {step.complete ? '✓' : '○'} {step.label}
          </li>
        ))}
      </ol>
      {progress.generationStatus && (
        <p className="text-xs text-muted-foreground">
          Worker: {progress.generationStatus.replace(/_/g, ' ').toLowerCase()}
        </p>
      )}
      {progress.error && (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-950"
        >
          {progress.error}
        </p>
      )}
      {!!progress.corrections?.length && (
        <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
          {progress.corrections.map((correction, i) => (
            <li key={i}>{correction}</li>
          ))}
        </ul>
      )}
    </div>
  )
}
export function WorldLibraryPage({ creationEnabled }: { creationEnabled?: boolean }) {
  const [params] = useSearchParams()
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [offset, setOffset] = useState(0)
  const [selected, setSelected] = useState<World | null>(null)
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const qc = useQueryClient()
  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(search.trim())
      setOffset(0)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])
  const library = useQuery({
    queryKey: ['world-library', q, status, offset],
    queryFn: async () =>
      (await apiClient.get<Page>('/api/v1/worlds/library', { params: { q, status, offset } })).data,
    refetchInterval: 5000,
    retry: false,
  })
  useEffect(() => {
    setSelected((current) =>
      current ? library.data?.items.find((world) => world.id === current.id) || current : null
    )
  }, [library.data])
  const live = useQuery({
    queryKey: ['world-progress', selected?.id],
    queryFn: async () =>
      (await apiClient.get<Progress>(`/api/v1/worlds/library/${selected!.id}/progress`)).data,
    enabled: !!selected,
    refetchInterval: 5000,
    retry: false,
  })
  const preview = useQuery({
    queryKey: ['world-preview', selected?.id],
    queryFn: async () =>
      (await apiClient.get<Preview>(`/api/v1/worlds/library/${selected!.id}/preview`)).data,
    enabled: !!selected && !!(selected.preview_ready || live.data?.previewReady),
    retry: false,
    staleTime: 300000,
  })
  if (params.has('world')) return <WorldStudioPage embedded />
  return (
    <section className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold">World library</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Follow automatic builds, preview on desktop or phone, then choose which worlds become
          films. Refreshes every 5 seconds.
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <label className="min-w-0 flex-1 text-sm">
          Search worlds
          <input
            aria-label="Search worlds"
            type="search"
            placeholder="Artwork, artist, concept or run ID…"
            maxLength={120}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="mt-1 min-h-11 w-full rounded-lg border bg-background px-3"
          />
        </label>
        <label className="text-sm">
          Build status
          <select
            aria-label="World build status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value)
              setOffset(0)
            }}
            className="mt-1 block min-h-11 rounded-lg border bg-background px-3"
          >
            <option value="">All worlds</option>
            <option value="building">Building</option>
            <option value="preview">Preview available</option>
            <option value="complete">Complete</option>
            <option value="attention">Needs attention</option>
          </select>
        </label>
      </div>
      {message && (
        <p role="status" className="rounded-xl border p-4 text-sm">
          {message}
        </p>
      )}
      {library.error && (
        <div role="alert">
          World library unavailable.{' '}
          <Button variant="outline" onClick={() => void library.refetch()}>
            Retry
          </Button>
        </div>
      )}
      {library.data && !library.data.items.length && (
        <div className="rounded-xl border border-dashed p-6">
          <p>No builds match this view.</p>
          <Link className="mt-2 block text-sm underline" to="/app/content-studio?view=ideas">
            Choose concepts and create your first world →
          </Link>
        </div>
      )}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {library.data?.items.map((world) => (
          <article key={world.id} className="space-y-3 rounded-2xl border bg-card p-4">
            <ArtworkPreview title={world.title} sourceUrl={world.source_image_url} />
            <span
              className={`inline-block rounded-full px-2.5 py-1 text-xs ${world.complete ? 'bg-emerald-100 text-emerald-900' : 'bg-muted'}`}
            >
              {world.complete
                ? 'World complete'
                : world.preview_ready
                  ? 'Preview ready · finishing assets'
                  : world.stage.replace(/_/g, ' ')}
            </span>
            <h3 className="font-semibold">{world.title}</h3>
            <p className="text-sm text-muted-foreground">{world.artist}</p>
            <WorldBuildProgress progress={world.progress} />
            <Button
              variant="outline"
              className="min-h-11 w-full"
              onClick={() => {
                setSelected(world)
                setMessage('')
              }}
            >
              {world.preview_ready ? 'Preview / QR & details' : 'Live build details'}
            </Button>
          </article>
        ))}
      </div>
      {library.data && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <Button
            variant="outline"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - library.data!.limit))}
          >
            Previous
          </Button>
          <span>
            {library.data.total ? `${offset + 1}–${offset + library.data.items.length}` : '0'} of{' '}
            {library.data.total} builds
          </span>
          <Button
            variant="outline"
            disabled={offset + library.data.limit >= library.data.total}
            onClick={() => setOffset(offset + library.data!.limit)}
          >
            Next
          </Button>
        </div>
      )}
      <details className="rounded-xl border p-4">
        <summary className="cursor-pointer text-sm">Advanced: legacy world screening</summary>
        <p className="mt-2 text-xs text-muted-foreground">
          The library indexes managed creation runs. Historical standalone review bundles remain
          here.
        </p>
        <WorldStudioPage embedded />
      </details>
      <Dialog
        open={!!selected}
        onOpenChange={(open) => {
          if (!open && !busy) setSelected(null)
        }}
      >
        <DialogContent className="max-h-[90dvh] max-w-2xl overflow-y-auto font-sans">
          <DialogHeader>
            <DialogTitle>{selected?.title}</DialogTitle>
            <DialogDescription>
              {selected?.artist} · Automatic world production; films are optional.
            </DialogDescription>
          </DialogHeader>
          {selected && (
            <>
              <WorldBuildProgress progress={live.data || selected.progress} />
              {live.error && (
                <p role="alert">Live state unavailable; showing the last library snapshot.</p>
              )}
              {preview.data && (
                <div className="flex flex-wrap items-center gap-5 rounded-xl border p-4">
                  <img
                    src={preview.data.qrDataUrl}
                    alt={`Mobile preview QR for ${selected.title}`}
                    width={192}
                    height={192}
                  />
                  <div className="space-y-3">
                    <Button asChild className="min-h-11">
                      <a href={preview.data.previewUrl} target="_blank" rel="noopener noreferrer">
                        Open immersive preview
                      </a>
                    </Button>
                    <p className="max-w-xs text-xs text-muted-foreground">
                      Scan with your phone camera. Public review preview, not a customer release. AR
                      device tracking is a separate validation.
                    </p>
                  </div>
                </div>
              )}
              {preview.error && (
                <p role="alert" className="text-sm">
                  Preview unavailable or changed; refresh before using it.
                </p>
              )}
              {(live.data || selected.progress).repairAvailable && (
                <Button
                  className="min-h-11"
                  disabled={busy || !creationEnabled}
                  onClick={async () => {
                    setBusy(true)
                    try {
                      await apiClient.post(`/api/v1/worlds/library/${selected.id}/repair`, {})
                      setMessage(
                        'Scene repair queued. The existing panorama is reused; a new bounded refinement and independent critique will run.'
                      )
                      await qc.invalidateQueries({ queryKey: ['world-library'] })
                      await live.refetch()
                    } catch (e) {
                      setMessage(
                        (e as { response?: { data?: { error?: string } } }).response?.data?.error ||
                          'Scene repair could not be queued'
                      )
                    } finally {
                      setBusy(false)
                    }
                  }}
                >
                  {busy ? 'Queuing repair…' : 'Repair scene & retry critique'}
                </Button>
              )}
              <p className="text-xs text-muted-foreground">
                Updated {new Date(selected.updated_at).toLocaleString()} · {selected.id}
              </p>
              {selected.film_job_id ? (
                <Button asChild>
                  <Link to={`/app/content-studio?view=films&job=${selected.film_job_id}`}>
                    Open film & review
                  </Link>
                </Button>
              ) : (
                <Button
                  className="min-h-11"
                  disabled={
                    busy ||
                    !creationEnabled ||
                    !(live.data || selected.progress).complete ||
                    (live.data || selected.progress).filmRequested
                  }
                  onClick={async () => {
                    setBusy(true)
                    try {
                      await apiClient.post(`/api/v1/worlds/library/${selected.id}/film`, {})
                      setMessage(
                        'Film requested. Production runs automatically; review the finished film before scheduling.'
                      )
                      await qc.invalidateQueries({ queryKey: ['world-library'] })
                      await live.refetch()
                    } catch (e) {
                      setMessage(
                        (e as { response?: { data?: { error?: string } } }).response?.data?.error ||
                          'Film request failed'
                      )
                    } finally {
                      setBusy(false)
                    }
                  }}
                >
                  {busy
                    ? 'Requesting…'
                    : (live.data || selected.progress).filmRequested
                      ? 'Film already requested'
                      : 'Create film from this world'}
                </Button>
              )}
              {!creationEnabled && (
                <p className="text-sm text-muted-foreground">
                  Film production is paused by the administrator.
                </p>
              )}
              {message && (
                <p role="status" className="text-sm">
                  {message}
                </p>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  )
}
